import { request } from "./http";

export interface PageLink {
  id: number;
  page_id: number;
  type?: string;
  label: string;
  url: string;
  whatsapp_phone?: string | null;
  whatsapp_message?: string | null;
  order_index: number;
}

export interface PageSocialLink {
  id: number;
  page_id: number;
  network: string;
  username: string;
  url: string;
  order_index: number;
}

export type ProfilePage = {
  id?: number;
  slug: string;
  title?: string | null;
  links_label?: string | null;
  show_links_label?: boolean | null;
  title_size?: "small" | "medium" | "large" | null;
  logo_base64?: string | null;
  description?: string | null;
  background_color?: string | null;
  instagram_url?: string | null;
  whatsapp_phone?: string | null;
  whatsapp_message?: string | null;
  links?: PageLink[];
  social_links?: PageSocialLink[];
  today_accesses?: number;
  updated_at?: string;
};

export type PageFormData = {
  id?: number;
  slug?: string;
  title?: string;
  links_label?: string;
  title_size?: "small" | "medium" | "large";
  logo_base64?: string;
  description?: string;
  background_color?: string;
  instagram_url?: string;
  whatsapp_phone?: string;
  whatsapp_message?: string;
};

type SavePayload = {
  id?: number;
  slug: string;
  title?: string;
  links_label?: string;
  title_size?: "small" | "medium" | "large";
  logo_base64?: string;
  description?: string;
  background_color?: string;
  instagram_url?: string;
  whatsapp_phone?: string;
  whatsapp_message?: string;
};

type DeletePayload = {
  id: number;
};

type LinkPayload = {
  id?: number;
  pageId?: number;
  label?: string;
  url?: string;
};

type SocialLinkPayload = {
  id?: number;
  pageId?: number;
  network: string;
  username: string;
};

export const savePage = (body: SavePayload) =>
  request<ProfilePage>("/api/pages", { method: "POST", body: JSON.stringify(body) });

export const deletePage = (body: DeletePayload) =>
  request<{}>("/api/pages", { method: "DELETE", body: JSON.stringify(body) });

export const getMyPages = () => request<ProfilePage[]>("/api/pages/me");

export const getMyPage = (slug: string) =>
  request<ProfilePage | null>(`/api/pages/me?slug=${encodeURIComponent(slug)}`);

export const getPublicPage = (slug: string) =>
  request<ProfilePage>(`/api/pages?slug=${encodeURIComponent(slug)}`);

export const recordPageAccess = (slug: string) =>
  request<{ success: boolean }>(`/api/pages/access?slug=${encodeURIComponent(slug)}`, { method: "POST" });

export const checkPageSlugAvailability = (slug: string, pageId?: number | null) => {
  const params = new URLSearchParams({ slug });
  if (typeof pageId === "number" && !Number.isNaN(pageId)) {
    params.set("pageId", String(pageId));
  }
  return request<{ available: boolean; message?: string }>(`/api/pages/availability?${params.toString()}`);
};

export const createPageLink = (body: LinkPayload) =>
  request<PageLink>("/api/page-links", { method: "POST", body: JSON.stringify(body) });

export const updatePageLink = (body: LinkPayload) =>
  request<PageLink>("/api/page-links", { method: "PUT", body: JSON.stringify(body) });

export const deletePageLink = (id: number) =>
  request<{}>(`/api/page-links/${id}`, { method: "DELETE" });

export const createPageSocialLink = (body: SocialLinkPayload) =>
  request<PageSocialLink>("/api/page-social-links", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePageSocialLink = (body: SocialLinkPayload) =>
  request<PageSocialLink>("/api/page-social-links", {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deletePageSocialLink = (id: number) =>
  request<{}>(`/api/page-social-links`, {
    method: "DELETE",
    body: JSON.stringify({ id }),
  });

export const getPageStats = (slug: string, period: string = "7d") =>
  request<Array<{ date: string; accesses: number }>>(`/api/pages/me/stats?slug=${encodeURIComponent(slug)}&period=${period}`);

export const getPageSubscriptionStatus = (slug: string) =>
  request<{ hasActiveSubscription: boolean }>(`/api/pages/subscription-status?slug=${encodeURIComponent(slug)}`);

